import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  CheckCircle, ChevronLeft, ChevronRight, Send, Loader2, Upload, FileText, X, Coins,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sortByOrder = (arr) => [...(arr || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
const DURATION_UNITS = ['Secondes', 'Minutes', 'Heures', 'Jours', 'Semaines', 'Mois', 'Annees'];

export default function FDVDynamicRenderer() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({}); // key -> File[]
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [prospectId, setProspectId] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/fdv-templates/active`, { credentials: 'include' });
        const data = await r.json();
        if (cancelled) return;
        data.steps = sortByOrder(data.steps).map((s) => ({ ...s, fields: sortByOrder(s.fields) }));
        setTemplate(data);
        // Pre-fill partner info from user
        setValues((prev) => ({
          ...prev,
          partner_firstname: user?.name?.split(' ')[0] || '',
          partner_lastname: user?.name?.split(' ').slice(1).join(' ') || '',
          partner_email: user?.email || '',
        }));
      } catch (e) {
        console.error('Failed to load FDV template', e);
        toast.error('Impossible de charger le formulaire FDV');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const pid = searchParams.get('prospect_id');
    if (pid) {
      setProspectId(pid);
      (async () => {
        try {
          const r = await fetch(`${API}/prospects/${pid}/prefill`, { credentials: 'include' });
          if (r.ok) {
            const d = await r.json();
            setValues((prev) => ({
              ...prev,
              establishment_name: d.establishment_name || prev.establishment_name || '',
              establishment_type: d.establishment_type || '',
              academic_region: d.academic_region || '',
              address_street: d.address_street || '',
              address_city: d.address_city || '',
              address_postal: d.address_postal || '',
              contact_name: d.contact_name || '',
              contact_function: d.contact_function || '',
              contact_email: d.contact_email || '',
              contact_phone: d.contact_phone || '',
            }));
            toast.success('Donnees du lead chargees');
          }
        } catch (e) { /* noop */ }
      })();
    }
  }, [searchParams]);

  const setValue = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const validateStep = (step) => {
    const errors = [];
    for (const f of step.fields) {
      if (!f.required) continue;
      if (f.type === 'file') {
        if (!(files[f.key] && files[f.key].length > 0)) errors.push(f.label);
      } else if (f.type === 'duration') {
        const d = values[f.key];
        const units = (f.units && f.units.length) ? f.units : [];
        const ok = d && typeof d === 'object' && units.length > 0 && units.every((u) => String(d[u] ?? '').trim() !== '');
        if (!ok) errors.push(f.label);
      } else if (f.type === 'checkbox' && f.options && f.options.length) {
        const arr = values[f.key];
        if (!Array.isArray(arr) || arr.length === 0) errors.push(f.label);
      } else {
        const v = values[f.key];
        if (v === undefined || v === null || String(v).trim() === '') errors.push(f.label);
      }
    }
    return errors;
  };

  const steps = template?.steps || [];
  const totalSteps = steps.length;
  const progress = totalSteps ? ((currentStep + 1) / totalSteps) * 100 : 0;

  const next = () => {
    const errors = validateStep(steps[currentStep]);
    if (errors.length > 0) {
      toast.error(`Champs obligatoires manquants: ${errors.join(', ')}`);
      return;
    }
    if (currentStep < totalSteps - 1) setCurrentStep((s) => s + 1);
  };
  const prev = () => currentStep > 0 && setCurrentStep((s) => s - 1);

  const addFiles = (key, fileList, multiple) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setFiles((prev) => {
      const existing = prev[key] || [];
      return { ...prev, [key]: multiple ? [...existing, ...arr] : [arr[0]] };
    });
  };
  const removeFile = (key, idx) => {
    setFiles((prev) => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async () => {
    const errors = validateStep(steps[currentStep]);
    if (errors.length > 0) {
      toast.error(`Champs obligatoires manquants: ${errors.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('sale_data', JSON.stringify({
        template_id: template.template_id,
        data: values,
        prospect_id: prospectId,
      }));
      Object.entries(files).forEach(([key, arr]) => {
        (arr || []).forEach((file) => fd.append(key, file));
      });
      const r = await fetch(`${API}/sales/declare-with-docs`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (r.ok) {
        setSubmitted(true);
        toast.success('Vente declaree avec succes !');
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Erreur lors de la declaration');
      }
    } catch (e) {
      console.error('Submit error', e);
      toast.error('Erreur lors de la declaration');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="fdv-renderer-loading">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2 font-[Manrope]">
              Vente declaree avec succes !
            </h2>
            <p className="text-slate-500 mb-6">
              Votre declaration a ete enregistree. Vous pouvez la suivre dans "Mes ventes".
            </p>
            <Button
              data-testid="fdv-new-declaration-btn"
              onClick={() => { setSubmitted(false); setValues({}); setFiles({}); setCurrentStep(0); }}
            >
              Nouvelle declaration
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const step = steps[currentStep];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="declare-sale-title">
          Declarer une vente
        </h1>
        <p className="text-slate-500 mt-1">{template?.name} — remplissez le formulaire etape par etape</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-600" data-testid="fdv-step-indicator">
              Etape {currentStep + 1} sur {totalSteps}
            </span>
            <span className="text-sm font-medium text-indigo-600">{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-[Manrope]">{step?.title}</CardTitle>
          {step?.description && <CardDescription>{step.description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {step?.fields.map((f) => (
              <FieldRenderer
                key={f.field_id}
                field={f}
                value={values[f.key]}
                files={files[f.key] || []}
                setValue={setValue}
                addFiles={addFiles}
                removeFile={removeFile}
                fileRefs={fileRefs}
              />
            ))}
          </div>

          <div className="flex justify-between pt-6 border-t">
            <Button variant="outline" onClick={prev} disabled={currentStep === 0} data-testid="prev-step-btn">
              <ChevronLeft className="w-4 h-4 mr-2" /> Precedent
            </Button>
            {currentStep < totalSteps - 1 ? (
              <Button onClick={next} data-testid="next-step-btn">
                Suivant <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="submit-sale-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Declarer la vente
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FieldRenderer({ field, value, files, setValue, addFiles, removeFile, fileRefs }) {
  const f = field;
  const wide = ['textarea', 'radio', 'file', 'slider', 'montant_total', 'checkbox', 'duration'].includes(f.type);
  const testId = `fdv-field-${f.key}`;

  const label = (
    <Label className="flex items-center gap-2">
      {f.type === 'montant_total' && <Coins className="w-4 h-4 text-emerald-600" />}
      {f.label}{f.required && ' *'}
    </Label>
  );

  let control = null;
  switch (f.type) {
    case 'textarea':
      control = (
        <Textarea
          data-testid={testId}
          value={value || ''}
          placeholder={f.placeholder || ''}
          onChange={(e) => setValue(f.key, e.target.value)}
        />
      );
      break;
    case 'number':
      control = (
        <Input
          type="number" data-testid={testId} value={value ?? ''}
          min={f.min ?? undefined} max={f.max ?? undefined}
          placeholder={f.placeholder || ''}
          onChange={(e) => setValue(f.key, e.target.value)}
        />
      );
      break;
    case 'montant_total':
      control = (
        <div className="space-y-2">
          <Input
            type="number" step="0.01" min="0" data-testid={testId} value={value ?? ''}
            placeholder="0.00"
            onChange={(e) => setValue(f.key, e.target.value)}
          />
          <span className="inline-block text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
            Servira au calcul des commissions
          </span>
        </div>
      );
      break;
    case 'date':
      control = (
        <Input type="date" data-testid={testId} value={value || ''} onChange={(e) => setValue(f.key, e.target.value)} />
      );
      break;
    case 'checkbox':
      if (f.options && f.options.length) {
        const arr = Array.isArray(value) ? value : [];
        const toggle = (opt) => {
          const has = arr.includes(opt);
          if (!has && f.max_selections && arr.length >= f.max_selections) {
            toast.error(`Maximum ${f.max_selections} choix autorise(s)`);
            return;
          }
          setValue(f.key, has ? arr.filter((o) => o !== opt) : [...arr, opt]);
        };
        control = (
          <div className="space-y-2" data-testid={testId}>
            {f.options.map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={arr.includes(opt)} onCheckedChange={() => toggle(opt)} data-testid={`${testId}-${opt}`} />
                {opt}
              </label>
            ))}
            {f.max_selections ? <p className="text-xs text-slate-400">Maximum {f.max_selections} choix</p> : null}
          </div>
        );
      } else {
        control = (
          <div className="flex items-center gap-2">
            <Checkbox
              data-testid={testId}
              checked={!!value}
              onCheckedChange={(c) => setValue(f.key, !!c)}
            />
            <span className="text-sm text-slate-600">{f.help_text || 'Oui'}</span>
          </div>
        );
      }
      break;
    case 'duration': {
      const d = (value && typeof value === 'object') ? value : {};
      const units = (f.units && f.units.length) ? f.units : DURATION_UNITS;
      control = (
        <div className="flex flex-wrap gap-3" data-testid={testId}>
          {units.map((u) => (
            <div key={u} className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">{u}</span>
              <Input type="number" min="0" className="w-28" value={d[u] ?? ''} placeholder="0"
                data-testid={`${testId}-${u}`}
                onChange={(e) => setValue(f.key, { ...d, [u]: e.target.value })} />
            </div>
          ))}
        </div>
      );
      break;
    }
    case 'radio':
      control = (
        <RadioGroup
          value={value || ''}
          onValueChange={(v) => setValue(f.key, v)}
          className="flex flex-wrap gap-4"
          data-testid={testId}
        >
          {(f.options || []).map((opt) => (
            <div key={opt} className="flex items-center space-x-2">
              <RadioGroupItem value={opt} id={`${f.key}-${opt}`} data-testid={`${testId}-${opt}`} />
              <Label htmlFor={`${f.key}-${opt}`}>{opt}</Label>
            </div>
          ))}
        </RadioGroup>
      );
      break;
    case 'select':
      control = (
        <Select value={value || ''} onValueChange={(v) => setValue(f.key, v)}>
          <SelectTrigger data-testid={testId}><SelectValue placeholder="Selectionner" /></SelectTrigger>
          <SelectContent>
            {(f.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;
    case 'slider': {
      const min = f.min ?? 0; const max = f.max ?? 100; const stp = f.step ?? 1;
      const current = typeof value === 'number' ? value : min;
      control = (
        <div className="space-y-2" data-testid={testId}>
          <Slider min={min} max={max} step={stp} value={[current]} onValueChange={(v) => setValue(f.key, v[0])} />
          <div className="text-sm text-slate-600">Valeur: <span className="font-medium">{current}</span></div>
        </div>
      );
      break;
    }
    case 'file':
      control = (
        <div className="space-y-2">
          <input
            type="file"
            multiple={!!f.multiple}
            ref={(el) => { fileRefs.current[f.key] = el; }}
            className="hidden"
            data-testid={`${testId}-input`}
            onChange={(e) => { addFiles(f.key, e.target.files, !!f.multiple); e.target.value = ''; }}
          />
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((file, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="flex-1 text-emerald-700 font-medium text-sm truncate">{file.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeFile(f.key, idx)} data-testid={`${testId}-remove-${idx}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {(f.multiple || files.length === 0) && (
            <Button
              variant="outline"
              className="w-full h-16 border-dashed"
              onClick={() => fileRefs.current[f.key]?.click()}
              data-testid={`${testId}-btn`}
            >
              <Upload className="w-5 h-5 mr-2" /> {files.length === 0 ? 'Telecharger un fichier' : 'Ajouter un fichier'}
            </Button>
          )}
        </div>
      );
      break;
    default: // text
      control = (
        <Input
          data-testid={testId}
          value={value || ''}
          placeholder={f.placeholder || ''}
          onChange={(e) => setValue(f.key, e.target.value)}
        />
      );
  }

  return (
    <div className={`space-y-2 ${wide ? 'md:col-span-2' : ''}`}>
      <div className="flex items-center gap-2">
        {f.type === 'file' && <FileText className="w-4 h-4 text-indigo-600" />}
        {label}
      </div>
      {control}
      {f.help_text && f.type !== 'checkbox' && f.type !== 'montant_total' && (
        <p className="text-xs text-slate-400">{f.help_text}</p>
      )}
    </div>
  );
}
